import api from './api';
import { Lesson, Exercise, LessonProgress } from '../store/slices/lessonsSlice';

export interface LessonFilters {
  type?: string;
  difficulty?: string;
  completed?: boolean;
  search?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface LessonsResponse {
  lessons: Lesson[];
  total: number;
  page: number;
  totalPages: number;
}

export interface ExerciseSubmission {
  exerciseId: string;
  lessonId: string;
  answer: string | string[];
  timeSpent: number;
}

export interface ExerciseResult {
  exerciseId: string;
  isCorrect: boolean;
  score: number;
  explanation?: string;
  correctAnswer: string | string[];
}

export interface LessonCompletion {
  lessonId: string;
  score: number;
  timeSpent: number;
  completedAt: string;
}

class LessonsService {
  // Get lessons with optional filters and pagination
  async getLessons(filters?: LessonFilters, pagination?: PaginationParams): Promise<LessonsResponse> {
    const params = new URLSearchParams();

    if (filters?.type) params.append('type', filters.type);
    if (filters?.difficulty) params.append('difficulty', filters.difficulty);
    if (filters?.completed !== undefined) params.append('completed', filters.completed.toString());
    if (filters?.search) params.append('search', filters.search);
    if (pagination?.page) params.append('page', pagination.page.toString());
    if (pagination?.limit) params.append('limit', pagination.limit.toString());

    const response = await api.get(`/lessons?${params.toString()}`);
    return response.data;
  }

  // Get a specific lesson by ID
  async getLessonById(id: string): Promise<Lesson> {
    const response = await api.get(`/lessons/${id}`);
    return response.data;
  }

  // Get lesson progress for current user
  async getLessonProgress(lessonId?: string): Promise<LessonProgress[]> {
    const url = lessonId ? `/lessons/progress/${lessonId}` : '/lessons/progress';
    const response = await api.get(url);
    return response.data;
  }

  // Submit exercise answer
  async submitExercise(submission: ExerciseSubmission): Promise<ExerciseResult> {
    const response = await api.post('/lessons/exercises/submit', submission);
    return response.data;
  }

  // Complete a lesson
  async completeLesson(completion: LessonCompletion): Promise<LessonProgress> {
    const response = await api.post('/lessons/complete', completion);
    return response.data;
  }

  // Get user's lesson statistics
  async getLessonStats(): Promise<{
    totalLessons: number;
    completedLessons: number;
    totalPoints: number;
    averageScore: number;
    totalStudyTime: number;
    streakDays: number;
  }> {
    const response = await api.get('/lessons/stats');
    return response.data;
  }

  // Get recommended lessons based on user progress
  async getRecommendedLessons(): Promise<Lesson[]> {
    const response = await api.get('/lessons/recommended');
    return response.data;
  }

  // Get lesson categories
  async getLessonCategories(): Promise<string[]> {
    const response = await api.get('/lessons/categories');
    return response.data;
  }

  // Get lesson types
  async getLessonTypes(): Promise<string[]> {
    const response = await api.get('/lessons/types');
    return response.data;
  }

  // Get lesson difficulties
  async getLessonDifficulties(): Promise<string[]> {
    const response = await api.get('/lessons/difficulties');
    return response.data;
  }

  // Start a lesson (track start time)
  async startLesson(lessonId: string): Promise<void> {
    await api.post(`/lessons/${lessonId}/start`);
  }

  // Pause a lesson
  async pauseLesson(lessonId: string): Promise<void> {
    await api.post(`/lessons/${lessonId}/pause`);
  }

  // Resume a lesson
  async resumeLesson(lessonId: string): Promise<void> {
    await api.post(`/lessons/${lessonId}/resume`);
  }

  // Get lesson leaderboard
  async getLessonLeaderboard(lessonId: string): Promise<
    {
      rank: number;
      score: number;
      timeSpent: number;
      completedAt: string;
      user: {
        id: string;
        name: string;
        avatar?: string;
      };
    }[]
  > {
    const response = await api.get(`/lessons/${lessonId}/leaderboard`);
    return response.data;
  }

  // Bookmark a lesson
  async bookmarkLesson(lessonId: string): Promise<void> {
    await api.post(`/lessons/${lessonId}/bookmark`);
  }

  // Remove bookmark from lesson
  async removeBookmark(lessonId: string): Promise<void> {
    await api.delete(`/lessons/${lessonId}/bookmark`);
  }

  // Get bookmarked lessons
  async getBookmarkedLessons(): Promise<Lesson[]> {
    const response = await api.get('/lessons/bookmarked');
    return response.data;
  }

  // Rate a lesson
  async rateLesson(lessonId: string, rating: number): Promise<void> {
    await api.post(`/lessons/${lessonId}/rate`, { rating });
  }

  // Get lesson reviews
  async getLessonReviews(lessonId: string): Promise<
    {
      id: string;
      rating: number;
      comment?: string;
      user: {
        id: string;
        name: string;
        avatar?: string;
      };
      createdAt: string;
    }[]
  > {
    const response = await api.get(`/lessons/${lessonId}/reviews`);
    return response.data;
  }
}

export default new LessonsService();
